import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import AdLibraryService from 'services/AdLibraryService';
import { AnalysisPrompt } from './types';

const service = new AdLibraryService();

const PLACEHOLDERS = ['{{TRANSCRIPT}}', '{{HEADLINE}}', '{{PAGE_NAME}}', '{{CTA}}'];

export default function PromptEditor() {
  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    const data = await service.listPrompts();
    if (data) setPrompts(data.prompts);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  const selected = prompts.find(p => p.promptId === selectedId);

  useEffect(() => {
    if (selected) {
      setEditName(selected.name);
      setEditText(selected.promptText);
    }
  }, [selected]);

  const handleNew = async () => {
    const result = await service.createPrompt({
      name: 'New Prompt',
      promptText: 'Analyze this ad transcript:\n\n{{TRANSCRIPT}}\n\nHeadline: {{HEADLINE}}\nPage: {{PAGE_NAME}}\nCTA: {{CTA}}',
    });
    if (result) {
      await fetchPrompts();
      setSelectedId(result.promptId);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    await service.updatePrompt(selectedId, { name: editName, promptText: editText });
    await fetchPrompts();
    setSaving(false);
  };

  const handleDelete = async (promptId: string) => {
    await service.deletePrompt(promptId);
    if (selectedId === promptId) setSelectedId(null);
    fetchPrompts();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 500 }}>
      {/* Left: Prompt list */}
      <Paper variant="outlined" sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2">Prompts</Typography>
          <IconButton size="small" onClick={handleNew}><AddIcon /></IconButton>
        </Box>
        <List dense sx={{ overflow: 'auto', flex: 1 }}>
          {prompts.map((p) => (
            <ListItemButton
              key={p.promptId}
              selected={selectedId === p.promptId}
              onClick={() => setSelectedId(p.promptId)}
            >
              <ListItemText primary={p.name} secondary={p.isDefault ? 'Default' : undefined} />
              <IconButton size="small" edge="end" onClick={(e) => { e.stopPropagation(); handleDelete(p.promptId); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          ))}
          {prompts.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No prompts yet</Typography>
            </Box>
          )}
        </List>
      </Paper>

      {/* Right: Editor */}
      <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {selectedId ? (
          <>
            <TextField
              label="Prompt Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {PLACEHOLDERS.map((ph) => (
                <Chip
                  key={ph}
                  label={ph}
                  size="small"
                  variant="outlined"
                  onClick={() => setEditText(prev => prev + ph)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
            <TextField
              label="Prompt Text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              multiline
              minRows={12}
              maxRows={18}
              fullWidth
              sx={{ flex: 1 }}
            />
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Save
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'text.secondary' }}>
            <Typography>Select a prompt to edit, or create a new one</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
